import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import type { BillingAuditEntry, BillingAuditResponse } from "@/types/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatMoney } from "@/lib/billing-format";
import { formatDateTime } from "@/lib/utils";

/** Operator actions that have a translated label; Stripe event types show as-is. */
const OPERATOR_ACTIONS = [
  "amount_change",
  "one_off_invoice",
  "credit_grant",
  "billing_config_update",
  "invoice_mark_paid",
  "invoice_reminder",
  "invoice_credit_note",
  "invoice_refund",
  "invoice_void",
  "subscription_create",
  "subscription_cancel",
  "subscription_resume",
  "portal_link",
  "stripe_sync",
] as const;
type OperatorAction = (typeof OPERATOR_ACTIONS)[number];

const isOperatorAction = (a: string): a is OperatorAction =>
  (OPERATOR_ACTIONS as readonly string[]).includes(a);

function entryCurrency(entry: BillingAuditEntry): string {
  const c = entry.details.currency;
  return typeof c === "string" && c ? c : "EUR";
}

export function InstanceBillingAuditCard({ name }: { name: string }) {
  const { t } = useTranslation("billing");
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["instance", name, "billing-audit"],
      queryFn: ({ pageParam }) =>
        api.get<BillingAuditResponse>(
          `/api/admin/billing/instances/${name}/audit?limit=20${
            pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ""
          }`
        ),
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    });

  const entries = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("auditLog.title")}</CardTitle>
        <CardDescription>{t("auditLog.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : error ? (
          <p className="text-sm text-destructive">
            {t("loadFailed", { message: error instanceof Error ? error.message : String(error) })}
          </p>
        ) : entries.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("auditLog.empty")}</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("auditLog.columns.date")}</TableHead>
                    <TableHead>{t("auditLog.columns.actor")}</TableHead>
                    <TableHead>{t("auditLog.columns.action")}</TableHead>
                    <TableHead>{t("auditLog.columns.object")}</TableHead>
                    <TableHead className="text-right">{t("auditLog.columns.amount")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDateTime(e.createdAt)}
                      </TableCell>
                      <TableCell className="text-xs">{e.actor}</TableCell>
                      <TableCell>
                        {isOperatorAction(e.action) ? (
                          t(`auditLog.actions.${e.action}`)
                        ) : (
                          <span className="font-mono text-xs">{e.action}</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {e.stripeObjectId ?? "—"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right tabular-nums">
                        {e.amountCents === null ? "—" : formatMoney(e.amountCents, entryCurrency(e))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {hasNextPage ? (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {t("auditLog.loadMore")}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
